/**
 * MiniEntityCard, FullWidthEntityCard, RelatedEntitiesList for the structure map.
 */

import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { cn } from "@/utils/misc";
import { Badge } from "@/ui/primitives/badge";
import type { EntityItem } from "../types";
import { TYPE_ICON, STRUCTURE_MAP_TYPE_COLORS } from "../constants";
import { SourceDocumentLinks } from "./SectionHeader";
import { EntityDetailText } from "./EntityDetailText";

export function MiniEntityCard({
  entity,
  onDocumentClick,
}: {
  entity: EntityItem;
  onDocumentClick?: (documentId: string) => void;
}) {
  const Icon = TYPE_ICON[entity.type] ?? HelpCircle;
  const styles =
    STRUCTURE_MAP_TYPE_COLORS[entity.type] ?? STRUCTURE_MAP_TYPE_COLORS.company;

  return (
    <div
      className={cn(
        "flex items-start gap-2.5 rounded-xl border border-border bg-background px-3.5 py-2.5",
        "shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]",
        "hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]",
        "transition-all duration-200",
      )}
    >
      <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", styles.text)} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start gap-1.5">
          <p className="text-sm font-medium leading-snug">{entity.name}</p>
          {entity.isCurrent === true && (
            <Badge
              color="green"
              variant="soft"
              className="shrink-0 px-1 py-0 text-[9px]"
            >
              Current
            </Badge>
          )}
          {entity.isCurrent === false && (
            <Badge
              color="amber"
              variant="soft"
              className="shrink-0 px-1 py-0 text-[9px]"
            >
              Former
            </Badge>
          )}
        </div>
        {entity.detail && <EntityDetailText detail={entity.detail} />}
        {(entity.acn || entity.abn) && (
          <p className="mt-1 font-mono text-[11px] text-muted-foreground">
            {entity.acn && `ACN ${entity.acn}`}
            {entity.acn && entity.abn && " · "}
            {entity.abn && `ABN ${entity.abn}`}
          </p>
        )}
        <SourceDocumentLinks
          sourceDocuments={entity.sourceDocuments}
          onDocumentClick={onDocumentClick}
          className="mt-1.5"
        />
      </div>
    </div>
  );
}

export function FullWidthEntityCard({
  entity,
  onDocumentClick,
}: {
  entity: EntityItem;
  onDocumentClick?: (documentId: string) => void;
}) {
  const Icon = TYPE_ICON[entity.type] ?? HelpCircle;
  const styles =
    STRUCTURE_MAP_TYPE_COLORS[entity.type] ?? STRUCTURE_MAP_TYPE_COLORS.company;

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-xl border border-border bg-background px-4 py-3",
        "shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]",
        "hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]",
        "transition-all duration-200",
      )}
    >
      <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", styles.text)} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start gap-2">
          <p className="text-sm font-medium">{entity.name}</p>
          <Badge
            variant="outline"
            className={cn("shrink-0 text-[10px] capitalize", styles.text)}
          >
            {entity.type}
          </Badge>
          {entity.isCurrent === true && (
            <Badge
              color="green"
              variant="soft"
              className="shrink-0 text-[10px]"
            >
              Current
            </Badge>
          )}
          {entity.isCurrent === false && (
            <Badge
              color="amber"
              variant="soft"
              className="shrink-0 text-[10px]"
            >
              Former
            </Badge>
          )}
        </div>
        {entity.detail && <EntityDetailText detail={entity.detail} />}
        {(entity.acn || entity.abn) && (
          <p className="mt-1 font-mono text-[11px] text-muted-foreground">
            {entity.acn && `ACN ${entity.acn}`}
            {entity.acn && entity.abn && " · "}
            {entity.abn && `ABN ${entity.abn}`}
          </p>
        )}
        <SourceDocumentLinks
          sourceDocuments={entity.sourceDocuments}
          onDocumentClick={onDocumentClick}
          className="mt-1.5"
        />
      </div>
    </div>
  );
}

export function RelatedEntitiesList({
  entities,
  onDocumentClick,
}: {
  entities: EntityItem[];
  onDocumentClick?: (documentId: string) => void;
}) {
  const [showAll, setShowAll] = useState(entities.length <= 8);
  const visible = showAll ? entities : entities.slice(0, 8);
  const hiddenCount = entities.length - 8;

  return (
    <div>
      <div className="flex flex-col gap-2">
        {visible.map((entity, i) => (
          <FullWidthEntityCard
            key={entity.name + i}
            entity={entity}
            onDocumentClick={onDocumentClick}
          />
        ))}
      </div>
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="mt-2 cursor-pointer text-[11px] text-primary hover:underline"
        >
          {showAll ? "Show fewer" : `+ ${hiddenCount} more...`}
        </button>
      )}
    </div>
  );
}
